import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import SEO from "@/components/SEO";
import { api } from "@/lib/api";

const CATEGORIES = [
  "Erlebnisstandorte",
  "Hospitality & Guest Experience",
  "Digitale Markenräume",
  "Betreiberwissen",
  "Markt & Zukunft",
  "Developer & Content",
  "Partner & Sales",
];

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function Insights() {
  const [posts, setPosts] = useState([]);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/insights")
      .then((r) => setPosts(r.data.items || []))
      .finally(() => setLoading(false));
  }, []);

  const filtered = filter ? posts.filter((p) => p.category === filter) : posts;

  return (
    <>
      <SEO
        title="PINE Insights – Wissen für Erlebnisstandorte und digitale Markenräume"
        description="PINE Insights liefert Wissen zu Erlebnisstandorten, Hospitality, Location-Based Entertainment, Betreiberlogik, digitalen Markenräumen und PINE Systems."
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Blog",
          name: "PINE Insights",
          url: "https://www.pinexs.de/insights",
          inLanguage: "de-DE",
          publisher: { "@type": "Organization", name: "PINE Systems" },
        }}
      />

      <section className="relative border-b border-[rgba(255,255,255,0.08)] overflow-hidden">
        <div className="absolute inset-0 grid-overlay opacity-40" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-28 pb-16 md:pt-36 md:pb-20">
          <div className="overline" data-testid="insights-eyebrow">PINE Insights</div>
          <h1
            className="font-display text-4xl sm:text-5xl md:text-6xl uppercase mt-4 max-w-5xl leading-[1.04]"
            data-testid="insights-headline"
          >
            Wissen für <span className="text-[#18E096]">Erlebnisstandorte</span>, die funktionieren.
          </h1>
          <p className="text-[#AEB7B7] mt-6 max-w-3xl text-base md:text-lg leading-relaxed">
            Analysen, Impulse und Praxiswissen für Betreiber, Gründer, Hotels, Eventlocations,
            Entwickler und Partner, die moderne Erlebnisstandorte und digitale Markenräume
            verstehen und aufbauen wollen.
          </p>
          <p className="text-[#AEB7B7] mt-4 max-w-3xl text-sm md:text-base leading-relaxed">
            PINE Insights verbindet Marktverständnis, Betreiberlogik, Standortdenken und Customer
            Journey. Der Fokus liegt nicht auf Technik um der Technik willen, sondern auf
            Erlebnissen, die Gäste verstehen, buchen und weiterempfehlen können.
          </p>
        </div>
      </section>

      {/* Filter */}
      <section className="border-b border-[rgba(255,255,255,0.08)] bg-[#0B0F10]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8 flex flex-wrap gap-2 items-center">
          <span className="overline text-[#AEB7B7] mr-2">Kategorien</span>
          <button
            onClick={() => setFilter("")}
            data-testid="insights-filter-all"
            className={`text-xs uppercase tracking-[0.18em] px-3 py-2 border ${
              filter === ""
                ? "border-[#18E096] text-white"
                : "border-[rgba(255,255,255,0.12)] text-[#AEB7B7] hover:border-[#18E096]/60 hover:text-white"
            }`}
          >
            Alle
          </button>
          {CATEGORIES.map((c) => (
            <button
              key={c}
              onClick={() => setFilter(c)}
              data-testid={`insights-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`text-xs uppercase tracking-[0.18em] px-3 py-2 border ${
                filter === c
                  ? "border-[#18E096] text-white"
                  : "border-[rgba(255,255,255,0.12)] text-[#AEB7B7] hover:border-[#18E096]/60 hover:text-white"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </section>

      {/* List */}
      <section className="border-b border-[rgba(255,255,255,0.08)]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 md:py-24">
          {loading ? (
            <p className="text-[#AEB7B7]">Lädt…</p>
          ) : filtered.length === 0 ? (
            <p className="text-[#AEB7B7]" data-testid="insights-empty">
              Keine veröffentlichten Beiträge in dieser Kategorie. Schauen Sie bald wieder vorbei.
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px border-l border-t border-[rgba(255,255,255,0.08)]">
              {filtered.map((p) => (
                <Link
                  key={p.id}
                  to={`/insights/${p.slug}`}
                  className="border-r border-b border-[rgba(255,255,255,0.08)] p-8 hover:bg-[#0B0F10] transition-colors group"
                  data-testid={`insight-card-${p.slug}`}
                >
                  {p.cover_image_url ? (
                    <img
                      src={p.cover_image_url}
                      alt=""
                      className="w-full h-40 object-cover mb-6"
                    />
                  ) : (
                    <div className="w-full h-40 bg-gradient-to-br from-[#0B0F10] to-[#141819] mb-6 border-l-2 border-[#18E096]" />
                  )}
                  <div className="overline text-[#18E096]">{p.category || "PINE Insights"}</div>
                  <h2 className="font-display text-xl md:text-2xl uppercase mt-3 leading-tight text-white group-hover:text-[#18E096] transition-colors">
                    {p.title}
                  </h2>
                  <p className="text-[#AEB7B7] text-sm mt-4 leading-relaxed line-clamp-3">
                    {p.excerpt}
                  </p>
                  <div className="mt-6 flex items-center justify-between text-xs text-[#AEB7B7]">
                    <span>
                      {p.author || "PINE Systems"} · {formatDate(p.published_at)}
                    </span>
                    <ArrowUpRight
                      size={16}
                      strokeWidth={1.5}
                      className="text-[#18E096] group-hover:translate-x-1 transition-transform"
                    />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
